import { useContext, useMemo } from 'react'
import { Box, useTheme } from '@hermes/web-components'
import '@splidejs/react-splide/css'
import PageMeta from '../ProfileMeta'
import {
  HeroSection,
  HomePageLayout,
  Section,
  H1Title,
  H2Title,
  H3Title,
  H4Title,
  SecondarySection,
  BodyLarge
} from './styles'
import ProvidersSection from './ProvidersSection'
import RollAnimation from './RollAnimation'
import PeerEndorsementsSection from './PeerEndorsementsSection'
import ReviewsSection from './ReviewsSection'
import SpecialtiesSection from './SpecialtiesSection'
import TrustSection from './TrustSection'
import InsightItem from './InsightItem'
import VideoReviews from './VideoReviews'
import SearchBar from './SearchBar'
import { HomePageProps } from '../../types'
import { HomePageProvider } from '../../providers/HomePage'
import KeywordSection from './KeywordsSection'
import LocationsSection from './LocationsSection'
import useHomePageSchema from '../../hooks/useHomePageSchema'
import useHomePageMeta from '../../hooks/useHomePageMeta'
import useTranslateMessage from '../../hooks/useTranslateMessage'
import { AppDataContext } from '../../providers/AppData'
import { TENANTS } from '../../constants/global'
import { ROLL_ANIMATION_WORDS, ROLL_ANIMATION_WORDS_DE } from './constants'

const HomePage = ({
  defaultLocation,
  locationsAutocomplete,
  specialties,
  conditionsAndProcedures,
  reviews,
  endorsements,
  popularSpecialties,
  videoReviews,
  locationsForView,
  hospitalsForView,
  keywordsForView,
  customSpecialtiesSection,
  practicesAutocomplete,
  specialistsAutocomplete
}: HomePageProps) => {
  const { locale } = useContext(AppDataContext)
  const pageMeta = useHomePageMeta()
  const pageSchema = useHomePageSchema()
  const translate = useTranslateMessage()
  const theme = useTheme()
  const isGerman = useMemo(() => locale === TENANTS.DE || locale === TENANTS.AT, [locale])
  const rollItems = useMemo(() => (isGerman ? ROLL_ANIMATION_WORDS_DE : ROLL_ANIMATION_WORDS), [isGerman])

  const insightItems = useMemo(
    () => [
      {
        title: translate('homepage.insight.pledge.title'),
        subtitle: translate('homepage.insight.pledge.subtitle')
      },
      {
        title: translate('homepage.insight.doctors.title'),
        subtitle: translate('homepage.insight.doctors.subtitle')
      },
      {
        title: translate('homepage.insight.mean.title'),
        subtitle: translate('homepage.insight.mean.subtitle')
      },
      {
        title: translate('homepage.insight.secured.title'),
        subtitle: translate('homepage.insight.secured.subtitle')
      }
    ],
    [translate]
  )

  return (
    <>
      <PageMeta {...pageMeta}>
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: pageSchema }} />
      </PageMeta>
      <HomePageProvider
        conditionsAndProcedures={conditionsAndProcedures}
        specialties={specialties}
        defaultLocation={defaultLocation}
        locationsAutocomplete={locationsAutocomplete}
        practicesAutocomplete={practicesAutocomplete}
        specialistsAutocomplete={specialistsAutocomplete}
      >
        <HomePageLayout sx={{ paddingBottom: { xs: '60px', md: '100px' } }}>
          <HomePageLayout
            sx={{
              padding: 0,
              background: 'linear-gradient(135deg, #89acfe 20%, #eaeffc 100%)',
              backgroundRepeat: 'no-repeat',
              backgroundSize: 'cover'
            }}
          >
            <HeroSection
              sx={{
                paddingTop: {
                  xs: `50px`,
                  md: `120px`
                },
                gap: '132px',
                paddingBottom: '46px',
                paddingLeft: '16px',
                paddingRight: '16px',
                [theme.breakpoints.down('md')]: {
                  gap: '88px'
                }
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  width: '100%',
                  gap: '60px',
                  [theme.breakpoints.down('md')]: {
                    gap: isGerman ? '32px' : '16px'
                  }
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '16px',
                    alignItems: 'start',
                    textAlign: 'start',
                    [theme.breakpoints.down('md')]: {
                      textAlign: 'center',
                      alignItems: 'center'
                    }
                  }}
                >
                  <H2Title
                    sx={{
                      [theme.breakpoints.down('md')]: {
                        fontSize: '18px'
                      }
                    }}
                    variant="body1"
                  >
                    {translate('homepage.yourhealth.title')}
                  </H2Title>
                  <H1Title
                    sx={{
                      [theme.breakpoints.down('md')]: {
                        fontSize: '36px',
                        lineHeight: '48px'
                      }
                    }}
                    variant="h1"
                  >
                    {translate('homepage.search.title') + ' '}
                    <RollAnimation items={rollItems} />
                  </H1Title>
                  {customSpecialtiesSection ? (
                    <SpecialtiesSection specialties={popularSpecialties} variant="variant2" />
                  ) : null}
                </Box>
                <SearchBar />
              </Box>
              <Box sx={{ marginBottom: '32px' }}>
                <H3Title
                  sx={{
                    marginBottom: '12px',
                    [theme.breakpoints.down('md')]: {
                      fontSize: '24px',
                      lineHeight: '32px'
                    }
                  }}
                  variant="h2"
                >
                  {translate('homepage.search.review.title')}
                </H3Title>
                <BodyLarge variant="h3" sx={{ fontWeight: 400 }}>
                  {translate('homepage.search.review.subtitle')}
                </BodyLarge>
              </Box>
            </HeroSection>
          </HomePageLayout>
          <Box
            sx={{
              marginTop: '-90px',
              marginBottom: '24px',
              width: '100%',
              height: '100%',
              display: 'flex',
              justifyContent: 'center'
            }}
          >
            <ReviewsSection reviews={reviews} />
          </Box>
          <PeerEndorsementsSection endorsements={endorsements} />
          {customSpecialtiesSection ? null : <SpecialtiesSection specialties={popularSpecialties} />}
          <HomePageLayout
            sx={{
              background: 'linear-gradient(180deg, #eaeffc 0%, #fff 100%)',
              paddingBottom: '68px',
              paddingTop: isGerman ? '62px' : '100px'
            }}
          >
            {!isGerman && (
              <Section>
                <TrustSection />
              </Section>
            )}
            <SecondarySection>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px', alignItems: 'start' }}>
                <H3Title
                  variant="h2"
                  style={{
                    background: 'linear-gradient(90deg, #051851 0%, #2B59E0 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent'
                  }}
                  sx={{ marginTop: { xs: '24px', md: '108px' } }}
                >
                  {translate('homepage.promoting.title')}
                </H3Title>
                <Box
                  sx={{
                    display: 'flex',
                    gap: { xs: '0', sm: '48px' },
                    marginTop: { xs: '13px', md: '24px' },
                    flexWrap: { xs: 'wrap', xl: 'nowrap' },
                    justifyContent: { xs: 'center', md: 'flex-start' },
                    marginBottom: { xs: '0px', sm: '48px', md: '0px' }
                  }}
                >
                  {insightItems.map(({ title, subtitle }, index) => (
                    <InsightItem key={index} title={title} subtitle={subtitle} />
                  ))}
                </Box>
              </Box>
            </SecondarySection>

            <Section sx={{ marginTop: { xs: 0, md: '62px' } }}>
              <Box
                sx={{ display: 'flex', flexDirection: 'column', gap: { md: '32px', xs: '24px' }, alignItems: 'start' }}
              >
                <H4Title variant="h3">{translate('homepage.hear.title')}</H4Title>
                <VideoReviews videoReviews={videoReviews} />
              </Box>
            </Section>
          </HomePageLayout>
          <Box sx={{ marginBottom: { md: '52px', xs: '4px' }, width: '100%', height: '100%' }}>
            <ProvidersSection />
          </Box>
          <Section
            sx={{
              marginBottom: '12px',
              [theme.breakpoints.down('md')]: {
                marginTop: '24px'
              }
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px', alignItems: 'start' }}>
              <H4Title
                variant="h2"
                sx={{
                  textAlign: 'start'
                }}
              >
                {translate('homepage.find.title')}
              </H4Title>
              <LocationsSection locationsForView={locationsForView} hospitalsForView={hospitalsForView} />
            </Box>
          </Section>

          <Section sx={{ marginTop: { xs: '12px', md: '0' } }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px', alignItems: 'start' }}>
              <H4Title
                sx={{
                  textAlign: 'start'
                }}
                variant="h2"
              >
                {translate('homepage.procedures.title')}
              </H4Title>
              <KeywordSection keywordsForView={keywordsForView} />
            </Box>
          </Section>
        </HomePageLayout>
      </HomePageProvider>
    </>
  )
}

export default HomePage
